<script setup lang="ts">
import { computed, watch, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useCvUpload } from './useCvUpload';
import { useCvStore } from './cvUploadStore';
import AFButton from '@/components/AFButton.vue';
import CvUploadInput from './componenents/CvUploadInput.vue';
import { LINKEDIN_HELP } from '@/config/constants';
import AfModal from '@/components/overlays/AfModal.vue';
import { useMediaStore } from '../media/mediaStore';
import { useModalStore } from '../modalStore';
import { useAvatarStore } from '@/features/avatar/avatarStore';
import { useUserDataMutation } from '@/api/mutations/user/use-user-data.mutation';

const { smAndDown } = useDisplay();
const cvStore = useCvStore();
const mediaStore = useMediaStore();
const modalStore = useModalStore();
const avatarStore = useAvatarStore();
const { auth } = useMeQuery();
const { mutateAsync: updateUserData } = useUserDataMutation();

const uploader = useCvUpload();

// Open if first connection
const isFirstConnection = computed(
  () => !!auth.value?.user_data?.tutorial?.candidate_first_connection,
);

function close() {
  if (isFirstConnection.value) {
    if (avatarStore.hasAvatar) {
      // Avatar already set: skip the modal and end the tutorial ourselves
      updateUserData({ tutorial: { candidate_first_connection: false } });
    } else {
      mediaStore.showOnlyAvatarModal = true;
      mediaStore.openModal();
    }
  }
  cvStore.closeModal();
}

onMounted(() => {
  if (
    isFirstConnection.value &&
    !cvStore.alreadyUpload &&
    !modalStore.invitationCompanySharepool.visible &&
    !modalStore.cguUpdate.visible
  ) {
    cvStore.openModal();
  }
});

watch(
  () => [modalStore.invitationCompanySharepool.visible, modalStore.cguUpdate.visible],
  ([isInvitationVisible, cguModalVisible]) => {
    if (
      !isInvitationVisible &&
      !cguModalVisible &&
      isFirstConnection.value &&
      !cvStore.alreadyUpload
    ) {
      cvStore.openModal();
    } else if ((isInvitationVisible || cguModalVisible) && cvStore.isOpen) {
      cvStore.closeModal();
    }
  },
);
</script>

<template>
  <AfModal
    v-model="cvStore.isOpen"
    :width="600"
    :header="false"
    :fullscreen="smAndDown"
    :footer="false"
    @close="close"
  >
    <template #content>
      <div class="flex flex-col items-center p-3 gap-5 md:gap-6">
        <h2 class="text-h2 my-1 leading-normal">{{ $t('profile.cv.push.title') }}</h2>
        <div class="flex flex-col items-center gap-5">
          <p class="text-body-2 text-center font-light">{{ $t('profile.cv.push.body') }}</p>
          <CvUploadInput
            :file="uploader.selectedFile?.value"
            :isUploading="uploader.isUploading?.value"
            :error="uploader.error?.value"
            :setFile="uploader.setFile"
            :clearFile="uploader.clearFile"
          />
        </div>
        <!-- Navigations -->
        <div class="mt-1 w-full flex justify-center gap-4 md:flex-row flex-col-reverse">
          <AFButton type="button" variant="tertiary" @click="close">
            {{ $t('general.cancel') }}
          </AFButton>
          <AFButton
            variant="primary"
            :disabled="!uploader.selectedFile.value"
            :loading="uploader.isUploading.value"
            data-cy="submit-upload-cv"
            @click="uploader.submit"
          >
            {{ $t('general.tests.status.continue') }}
          </AFButton>
        </div>

        <p class="text-center text-body-4">
          {{ $t('profile.cv.helper.body_1') }}
          <a
            :href="LINKEDIN_HELP"
            target="_blank"
            class="underline hover:no-underline text-af-blue-600 font-medium"
            >{{ $t('profile.cv.helper.body_2') }}</a
          >
        </p>
      </div>
    </template>
  </AfModal>
</template>

<style scoped></style>
